import { computed, onMounted, onUnmounted, ref, watch, type ComputedRef, type Ref } from 'vue'
import type { MessageWithReply } from '../types/messageTypes'

function messageSortTime(message: MessageWithReply): number {
  return Date.parse(String(message.created_at || message.time || '')) || 0
}

export function canSelectMessageForForward(message: MessageWithReply): boolean {
  return Boolean(
    message.matrix_event_id
    && !message.is_deleted
    && !message.is_system,
  )
}

export function useMessageSelection(options: {
  chatId: Ref<number | string | undefined> | ComputedRef<number | string | undefined>
  canForward: Ref<boolean> | ComputedRef<boolean>
}) {
  const isSelectionMode = ref(false)
  const selectedByEventId = ref<Map<string, MessageWithReply>>(new Map())

  const selectedCount = computed(() => selectedByEventId.value.size)

  const selectedMessages = computed(() =>
    [...selectedByEventId.value.values()].sort(
      (a, b) => messageSortTime(a) - messageSortTime(b),
    ),
  )

  const selectedEventIds = computed(() =>
    selectedMessages.value
      .map(message => message.matrix_event_id)
      .filter((id): id is string => Boolean(id)),
  )

  function isSelected(message: MessageWithReply): boolean {
    const eventId = message.matrix_event_id
    return Boolean(eventId && selectedByEventId.value.has(eventId))
  }

  function clearSelection() {
    selectedByEventId.value = new Map()
    isSelectionMode.value = false
  }

  function enterSelection(message: MessageWithReply) {
    if (!options.canForward.value || !canSelectMessageForForward(message)) {
      return
    }

    const eventId = message.matrix_event_id!
    const next = new Map<string, MessageWithReply>()
    next.set(eventId, message)
    selectedByEventId.value = next
    isSelectionMode.value = true
  }

  function toggleMessage(message: MessageWithReply) {
    if (!options.canForward.value || !canSelectMessageForForward(message)) {
      return
    }

    if (!isSelectionMode.value) {
      enterSelection(message)
      return
    }

    const eventId = message.matrix_event_id!
    const next = new Map(selectedByEventId.value)
    if (next.has(eventId)) {
      next.delete(eventId)
    }
    else {
      next.set(eventId, message)
    }

    selectedByEventId.value = next
    if (next.size === 0) {
      isSelectionMode.value = false
    }
  }

  watch(
    () => options.chatId.value,
    () => {
      clearSelection()
    },
  )

  watch(
    () => options.canForward.value,
    canForward => {
      if (!canForward) {
        clearSelection()
      }
    },
  )

  function onKeydown(event: KeyboardEvent) {
    if (event.key !== 'Escape' || !isSelectionMode.value) {
      return
    }

    event.preventDefault()
    clearSelection()
  }

  onMounted(() => {
    window.addEventListener('keydown', onKeydown)
  })

  onUnmounted(() => {
    window.removeEventListener('keydown', onKeydown)
  })

  return {
    isSelectionMode,
    selectedCount,
    selectedMessages,
    selectedEventIds,
    isSelected,
    enterSelection,
    toggleMessage,
    clearSelection,
  }
}
